import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Switch,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    SettingsCard,
    SettingsCheckTable,
    SettingsFlowSplit,
    SettingsRow,
} from '../../../../components/settings/primitives';
import OemRebateTable from './OemRebateTable';
import {
    MARKETPLACE_LABELS,
    MARKETPLACE_OPTIONS,
    type OemDraft,
    type RebateMarketplace,
} from './oemDraft';

export interface OemRebateStepProps {
    draft: OemDraft;
    onChange: (next: OemDraft) => void;
}

/**
 * Second step, and optional: the OEM's rebate program. Nothing below the
 * enrolment toggle exists until it is on — including the tier table on the right
 * — so a make with no rebate is one switch and done.
 */
const OemRebateStep = ({ draft, onChange }: OemRebateStepProps) => {
    const { rebateEnabled } = draft;

    const handleToggleEnabled = (enabled: boolean) => {
        onChange({
            ...draft,
            rebateEnabled: enabled,
            // Every marketplace qualifies until told otherwise, so turning the
            // program on starts from all of them rather than none.
            marketplaces:
                enabled && draft.marketplaces.length === 0
                    ? [...MARKETPLACE_OPTIONS]
                    : draft.marketplaces,
        });
    };

    const handleToggleMarketplace = (id: string, checked: boolean) => {
        const marketplace = id as RebateMarketplace;
        onChange({
            ...draft,
            marketplaces: checked
                ? Array.from(new Set([...draft.marketplaces, marketplace]))
                : draft.marketplaces.filter((candidate) => candidate !== marketplace),
        });
    };

    const left = (
        <VStack align="stretch" spacing={6} flex={1} minH={0} px={6} pb={6} overflow="hidden">
            <SettingsCard title="Rebate program">
                <SettingsRow
                    label="Enrolled in a rebate program"
                    description="Turn on to record the tiers this OEM pays back and what an order has to go through to earn them."
                    control={
                        <Switch
                            size="lg"
                            colorScheme="partbay-blue"
                            isChecked={rebateEnabled}
                            onChange={(e) => handleToggleEnabled(e.target.checked)}
                            aria-label="Enrolled in a rebate program"
                        />
                    }
                />

                {/* The other yes/no about the program itself, so it sits under
                    the enrolment toggle rather than in a section of its own. */}
                {rebateEnabled && (
                    <SettingsRow
                        label="Override DRP thresholds"
                        description="The rebate through a qualifying marketplace counts for more than a carrier's ordering threshold."
                        control={
                            <Switch
                                size="lg"
                                colorScheme="partbay-blue"
                                isChecked={draft.overrideDrpThreshold}
                                onChange={(e) =>
                                    onChange({
                                        ...draft,
                                        overrideDrpThreshold: e.target.checked,
                                    })
                                }
                                aria-label="Override DRP thresholds"
                            />
                        }
                    >
                        {draft.overrideDrpThreshold && (
                            <Alert status="info" borderRadius="md" alignItems="flex-start">
                                <AlertIcon mt="2px" />
                                <AlertDescription fontSize="sm">
                                    You accept that this can push ordering below a carrier's
                                    required percentage. Quoting percentages are unaffected.
                                </AlertDescription>
                            </Alert>
                        )}
                    </SettingsRow>
                )}
            </SettingsCard>

            {rebateEnabled && (
                <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800" mb={1}>
                        Applicable marketplaces
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2.5}>
                        An order has to be placed through one of these to earn the rebate.
                        Uncheck any that do not count.
                    </Text>
                    <SettingsCheckTable
                        columnLabel="MARKETPLACE"
                        checkLabel="Qualifies"
                        emptyMessage="No marketplaces available"
                        rows={MARKETPLACE_OPTIONS.map((marketplace) => ({
                            id: marketplace,
                            label: MARKETPLACE_LABELS[marketplace],
                            isChecked: draft.marketplaces.includes(marketplace),
                        }))}
                        onToggle={handleToggleMarketplace}
                    />
                </Box>
            )}
        </VStack>
    );

    return (
        <SettingsFlowSplit
            leftWidth="50%"
            leftTitle="Rebates"
            left={left}
            isLeftFlush
            // The tier table only exists once there is a program to put tiers in.
            rightTitle={rebateEnabled ? 'Rebate tiers' : undefined}
            rightDescription={
                rebateEnabled
                    ? 'What the OEM pays back at each ordering threshold. Leave Max blank for the top tier.'
                    : undefined
            }
            right={
                rebateEnabled ? (
                    <Box flex={1} minH={0} px={6} pb={6} display="flex" flexDirection="column">
                        <OemRebateTable
                            tiers={draft.tiers}
                            onChange={(tiers) => onChange({ ...draft, tiers })}
                        />
                    </Box>
                ) : null
            }
            isRightFlush
        />
    );
};

export default OemRebateStep;
